import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * NiceSelect (dark-friendly, keyboard/mouse accessible)
 *
 * props:
 *  - options: any[]
 *  - value: any
 *  - onChange: (nextValue) => void
 *  - getLabel: (opt) => string
 *  - getValue: (opt) => string|number
 *  - placeholder?: string
 *  - disabled?: boolean
 *  - useNativeOnMobile?: boolean (default true)
 *  - className?: string
 */
export default function NiceSelect({
  options = [],
  value,
  onChange,
  getLabel = (o) => String(o?.label ?? o?.title ?? o ?? ""),
  getValue = (o) => String(o?.value ?? o?.id ?? o ?? ""),
  placeholder = "— Choose —",
  disabled = false,
  useNativeOnMobile = true,
  className = "",
}) {
  // --- Hooks must be called unconditionally and in the same order ---
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const btnRef = useRef(null);
  const listRef = useRef(null);

  const isTouch = useMemo(() => {
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
  }, []);
  const useNative = useNativeOnMobile && isTouch;

  const selectedIndex = useMemo(() => {
    const v = value == null ? "" : String(value);
    return options.findIndex((o) => String(getValue(o)) === v);
  }, [options, value, getValue]);

  // close when clicking outside (runs even if native is shown; harmless)
  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (
        !btnRef.current?.contains(e.target) &&
        !listRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [open]);

  // keep active item in view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  };

  const choose = (i) => {
    const opt = options[i];
    if (!opt) return;
    onChange?.(getValue(opt));
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open) {
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
        e.preventDefault();
        openList();
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      choose(activeIndex);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(options.length - 1, i + 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
      return;
    }
  };

  const label =
    selectedIndex >= 0 ? getLabel(options[selectedIndex]) : placeholder;

  // --- Render: branch only inside JSX (no conditional hooks) ---
  return useNative ? (
    <select
      className={"select-modern " + className}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o, i) => {
        const v = getValue(o);
        const l = getLabel(o);
        return (
          <option key={v ?? i} value={v}>
            {l}
          </option>
        );
      })}
    </select>
  ) : (
    <div className={"ns-wrap " + className} data-open={open ? "1" : "0"}>
      <button
        ref={btnRef}
        type="button"
        className="ns-btn"
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span
          className={`ns-label ${selectedIndex < 0 ? "ns-placeholder" : ""}`}
        >
          {label}
        </span>
        <span className="ns-caret">▾</span>
      </button>

      {open && (
        <div className="ns-pop" role="listbox" ref={listRef} tabIndex={-1}>
          {options.length === 0 && <div className="ns-empty">No options</div>}
          {options.map((o, i) => {
            const v = getValue(o);
            const l = getLabel(o);
            const selected = i === selectedIndex;
            const active = i === activeIndex;
            return (
              <div
                key={v ?? i}
                role="option"
                aria-selected={selected}
                data-active={active ? "true" : "false"}
                className={`ns-item ${selected ? "is-selected" : ""} ${
                  active ? "is-active" : ""
                }`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => choose(i)}
              >
                {l}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
